import React, { type CSSProperties, useEffect, useMemo, useState } from "react";
import { useMap } from "react-leaflet";
import { useMediaQuery, useTheme } from "@mui/material";
import { CustomControl } from "./CustomControl";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

/**
 * コントロールコンテナのスタイル
 * 背景やボーダーを透明にして、地図の左下に自然に溶け込むようにする
 * clear: "both" でフロートの影響を受けないようにする
 */
const controlContainerStyle: CSSProperties = {
  margin: 0,
  padding: 0,
  background: "transparent",
  boxShadow: "none",
  border: "none",
  clear: "both",
};

/**
 * 情報パネルのスタイル
 * 地図の左下角に配置されるため、右上のみ角丸にして角に接続しているように見せる
 * 左と下のボーダーを消すことで、地図の枠と一体化したデザインにする
 */
const panelStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "4px 6px",
  background: "#ffffff",
  color: "#111827",
  borderTopRightRadius: 8,
  border: "1px solid #d1d5db",
  borderLeft: "none",
  borderBottom: "none",
  boxShadow: "0 3px 8px rgba(0, 0, 0, 0.12)",
};

/**
 * 座標表示のスタイル
 * 等幅フォント的な見た目で座標値を表示し、読みやすさを確保
 * whiteSpace: "nowrap" で座標が途中で折り返されないようにする
 */
const coordinateStyle: CSSProperties = {
  fontSize: 12,
  letterSpacing: 0.15,
  whiteSpace: "nowrap",
};

/**
 * ズームレベル表示のスタイル
 * 座標よりもやや控えめな見た目で、補助的な情報として表示
 */
const zoomStyle: CSSProperties = {
  fontSize: 12,
  opacity: 0.9,
  whiteSpace: "nowrap",
};

/**
 * セクション区切り線のスタイル
 * ズームレベルと座標の間に視覚的な区切りを設ける
 */
const dividerStyle: CSSProperties = {
  width: 1,
  height: 16,
  background: "#d1d5db",
};

/**
 * コピーボタンのスタイル
 * コンパクトなサイズで、ホバー時のフィードバックを提供
 * transition でスムーズなホバーエフェクトを実現
 */
const copyButtonStyle: CSSProperties = {
  width: 20,
  height: 20,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 2,
  border: "1px solid #FFF",
  cursor: "pointer",
  transition: "background 0.15s ease, border-color 0.15s ease",
};

/**
 * コピー完了バッジのスタイル
 * 小さく控えめに表示し、一時的なフィードバックとして機能
 */
const copiedBadgeStyle: CSSProperties = {
  fontSize: 11,
};

/**
 * スマホ幅向けのコンパクト表示
 * 左下の表示幅を抑え、右下の出典ボタン・凡例と重ならないようにする
 */
const compactPanelStyle: CSSProperties = {
  ...panelStyle,
  gap: 4,
  padding: "2px 4px",
};
const compactTextStyle: CSSProperties = { fontSize: 10 };
const compactDividerStyle: CSSProperties = { ...dividerStyle, height: 12 };
const compactCopyButtonStyle: CSSProperties = {
  ...copyButtonStyle,
  width: 16,
  height: 16,
};

/**
 * 地図の中心座標とズームレベルを表示するコントロールコンポーネント
 *
 * 機能:
 * - 地図の中心座標をリアルタイムで表示（移動に追従）
 * - 現在のズームレベルを表示
 * - 座標をクリップボードにコピーする機能
 *
 * 使用場面:
 * - ユーザーが現在見ている位置を確認したい時
 * - 座標を他のアプリケーションに共有したい時
 * - 開発・デバッグ時に現在位置を把握したい時
 *
 * Leaflet地図の左下に配置され、地図の移動・ズームに追従して更新される
 */
export const MapCenterControl: React.FC = () => {
  const map = useMap();
  const theme = useTheme();
  // スマホ幅では文字・余白を詰めて表示幅を抑える
  const isCompact = useMediaQuery(theme.breakpoints.down("sm"));

  // 地図の中心座標を管理するstate
  // 初期値は現在の地図の中心座標
  const [center, setCenter] = useState(() => map.getCenter());

  // ズームレベルを管理するstate
  const [zoom, setZoom] = useState(() => map.getZoom());

  // コピー成功のフィードバック表示を管理するstate
  const [copied, setCopied] = useState(false);

  /**
   * 地図のmove/zoomイベントをリッスンして座標とズームを更新
   *
   * 理由:
   * - ユーザーが地図をドラッグすると中心座標が変わるため、リアルタイムで追従する必要がある
   * - ズーム変更時も中心座標が微妙にずれる可能性があるため、両方のイベントで座標を更新
   */
  useEffect(() => {
    const handleMove = (): void => {
      setCenter(map.getCenter());
    };
    const handleZoom = (): void => {
      setZoom(map.getZoom());
      // ズーム時も中心座標が変わる可能性があるため更新
      setCenter(map.getCenter());
    };

    map.on("move", handleMove);
    map.on("zoom", handleZoom);

    // クリーンアップ: コンポーネントのアンマウント時にイベントリスナーを解除
    // これを忘れるとメモリリークやエラーの原因になる
    return () => {
      map.off("move", handleMove);
      map.off("zoom", handleZoom);
    };
  }, [map]);

  /**
   * コピー完了のフィードバック表示を一定時間後に自動的に消す
   *
   * 理由:
   * - 「コピー済み」の表示がずっと残っていると邪魔になる
   * - 1.4秒後に自動的に非表示にすることで、適度なフィードバックを提供
   */
  useEffect(() => {
    if (!copied) return;

    const timer = window.setTimeout(() => {
      setCopied(false);
    }, 1400);

    // クリーンアップ: タイマーをクリアして重複実行を防ぐ
    return () => {
      window.clearTimeout(timer);
    };
  }, [copied]);

  /**
   * 表示用の座標テキストをメモ化
   *
   * 理由:
   * - toFixed(6)で小数点以下6桁に固定（約11cm精度、実用上十分）
   * - useMemoで不要な再計算を防ぎ、パフォーマンスを最適化
   */
  const coordinateText = useMemo(
    () => `${center.lat.toFixed(6)},${center.lng.toFixed(6)}`,
    [center]
  );

  /**
   * 座標をクリップボードにコピーするハンドラー
   *
   * navigator.clipboard APIを使用してセキュアにコピー
   * エラー時はコンソールに出力（ユーザーには影響させない）
   */
  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(coordinateText);
      setCopied(true);
    } catch (error) {
      console.error("座標のコピーに失敗しました", error);
      setCopied(false);
    }
  };

  return (
    <CustomControl
      position="bottomleft"
      container={{ style: controlContainerStyle }}
    >
      <div
        style={isCompact ? compactPanelStyle : panelStyle}
        data-testid="map-center-control"
        data-compact={isCompact}
      >
        {/* ズームレベル表示 */}
        <span
          style={isCompact ? { ...zoomStyle, ...compactTextStyle } : zoomStyle}
        >
          ズーム: {zoom}
        </span>

        {/* ズームと座標の区切り線 */}
        <span style={isCompact ? compactDividerStyle : dividerStyle} />

        {/* 中心座標表示（緯度,経度の形式） */}
        <span
          style={
            isCompact
              ? { ...coordinateStyle, ...compactTextStyle }
              : coordinateStyle
          }
        >
          {coordinateText}
        </span>

        {/*
          座標コピーボタン
          インラインでhover効果を実装（CSSの:hoverが使えないため）
        */}
        <button
          type="button"
          style={isCompact ? compactCopyButtonStyle : copyButtonStyle}
          onClick={() => {
            void handleCopy();
          }}
          aria-label="座標をコピー"
          onMouseEnter={(event) => {
            event.currentTarget.style.background = "#f3f4f6";
            event.currentTarget.style.borderColor = "#9ca3af";
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.background = "#f9fafb";
            event.currentTarget.style.borderColor = "#d1d5db";
          }}
        >
          <ContentCopyIcon
            fontSize="small"
            style={isCompact ? { fontSize: 14 } : undefined}
          />
        </button>

        {/* コピー成功時のフィードバック表示 */}
        {copied && <span style={copiedBadgeStyle}>コピー済み</span>}
      </div>
    </CustomControl>
  );
};
